/**
 * Client-side processing-duration estimate, derived from the uploaded file size
 * (the browser has it before the request completes). Feeds the §24 adaptive
 * countdown. INTERIM heuristic — the coefficients below are guesses to be
 * calibrated by Stage-5 scale testing (and eventually replaced by the §24
 * server-side `base_overhead + activity_count × per_activity_benchmark`).
 * @module dashboard/utils/estimateProcessing
 */

// Interim, to be calibrated by Stage-5 scale testing.
export const BASE_OVERHEAD_S = 4;
export const PER_BYTE_S = 0.0000018; // ~2 s per MB
export const MIN_S = 5;
export const MAX_S = 180;

/**
 * Estimate processing seconds from file size, clamped to [MIN_S, MAX_S].
 * @param {number} fileSizeBytes
 * @returns {number} seconds
 */
export function estimateSeconds(fileSizeBytes) {
  const size = Number.isFinite(fileSizeBytes) && fileSizeBytes > 0 ? fileSizeBytes : 0;
  const raw = BASE_OVERHEAD_S + size * PER_BYTE_S;
  return Math.min(MAX_S, Math.max(MIN_S, raw));
}

/**
 * v3.20 §2 — fold the server's queue state into the countdown estimate:
 *   own-size estimate + (jobs ahead) × (rolling recent-analysis average).
 * Jobs ahead = queued + in_flight at the moment of upload. When the server has
 * no rolling history yet, the caller's own estimate stands in as the per-job
 * proxy. Deliberately NOT clamped to MAX_S — a genuine queue wait may exceed
 * the single-file ceiling and the countdown should say so honestly. Resilient:
 * a missing/malformed status returns the own estimate unchanged (the endpoint
 * is best-effort; the §24 countdown behaviour never depends on it).
 *
 * @param {number} ownSeconds - estimateSeconds() for this upload
 * @param {{queued?: number, in_flight?: number, recent_avg_seconds?: number|null}|null} status
 * @returns {number} seconds
 */
export function foldQueueEstimate(ownSeconds, status) {
  const own = Number.isFinite(ownSeconds) && ownSeconds > 0 ? ownSeconds : MIN_S;
  if (!status || typeof status !== 'object') return own;
  const ahead = (Number.isFinite(status.queued) ? status.queued : 0)
    + (Number.isFinite(status.in_flight) ? status.in_flight : 0);
  if (ahead <= 0) return own;
  const per = Number.isFinite(status.recent_avg_seconds) && status.recent_avg_seconds > 0
    ? status.recent_avg_seconds
    : own;
  return own + ahead * per;
}
